import React, { useEffect, useState } from "react";
import { suggestMatchTimes } from "../lib/api";

/**
 * SmartScheduleSuggestions — collapsible block that surfaces up to 5
 * candidate match times based on the availability overlap between two
 * teams. Wired inside ScheduleMatchModal & TeamHqModal challenge flow.
 *
 * Props:
 *   fromTeamId / toTeamId — required
 *   onPick(scheduledAtIso) — called when the user clicks a suggestion
 */
export function SmartScheduleSuggestions({ fromTeamId, toTeamId, onPick, onToast }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    if (!fromTeamId || !toTeamId) return;
    setBusy(true);
    try {
      const r = await suggestMatchTimes(fromTeamId, toTeamId, 14);
      setData(r);
      if (!r?.suggestions?.length) {
        onToast?.("No overlapping slots in the next 14 days. Set team availability to unlock smart picks.");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't fetch suggestions.");
    } finally { setBusy(false); }
  };

  // First open auto-fetches.
  useEffect(() => {
    if (open && data === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="rounded-2xl border border-[#14b8a6]/30 bg-[#14b8a6]/5" data-testid="smart-schedule-block">
      <button type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="smart-schedule-toggle"
              className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left">
        <span className="flex items-center gap-2">
          <i className="fas fa-wand-magic-sparkles text-[#14b8a6]" />
          <span className="text-[11px] tracking-[3px] font-bold text-[#14b8a6]">SMART SCHEDULE</span>
          <span className="text-[11px] text-white/45">Suggest best times based on both teams' availability</span>
        </span>
        <i className={`fas fa-chevron-${open ? "up" : "down"} text-white/40 text-xs`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2" data-testid="smart-schedule-body">
          {busy && <p className="text-[11px] text-white/45">Computing overlap…</p>}
          {!busy && data && data.suggestions.length === 0 && (
            <p className="text-[11px] text-white/55" data-testid="smart-schedule-empty">
              No overlap in the next 14 days. Edit team availability under
              Team HQ to expand options.
            </p>
          )}
          {!busy && data && data.suggestions.length > 0 && (
            <ul className="space-y-1.5" data-testid="smart-schedule-list">
              {data.suggestions.map((s, i) => (
                <li key={s.scheduled_at}>
                  <button type="button"
                          onClick={() => onPick?.(s.scheduled_at)}
                          data-testid={`smart-schedule-pick-${i}`}
                          className="w-full flex items-center justify-between gap-2 p-2.5 rounded-xl bg-black/40 hover:bg-[#14b8a6]/15 border border-white/10 hover:border-[#14b8a6]/50 text-left transition">
                    <span className="flex items-center gap-2">
                      <i className="far fa-clock text-[#14b8a6] text-xs" />
                      <span className="text-sm text-white tabular-nums">{s.human}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-widest text-white/55 px-2 py-0.5 rounded-md bg-white/5 border border-white/10">
                        {s.day} · {s.slot}
                      </span>
                      <i className="fas fa-arrow-right text-[#14b8a6]" />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default SmartScheduleSuggestions;
